import { useEffect, useState, type JSX } from 'react';
import { useMedplum } from '@medplum/react';
import { 
  Paper, Title, Text, Progress, Group, Badge, 
  Stack, Button, Loader 
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { IntakeTaskSummary, TaskService } from '../../services/TaskService';
import { formatDate } from '../../utils/date';

/**
 * Props for MedplumProfile context that is assumed to be available
 */
interface MedplumProfileContextProps {
  profile: any;
}

export const useMedplumProfile = (): MedplumProfileContextProps => {
  // This is a placeholder - the actual implementation should come from your app context
  // For now, we'll just access the profile directly from medplum
  const medplum = useMedplum();
  const profile = medplum.getProfile();
  return { profile };
};

/**
 * Component that displays the intake process summary
 */
export function IntakeSummary(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { profile: patient } = useMedplumProfile();
  const [tasks, setTasks] = useState<IntakeTaskSummary[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Load tasks when component mounts
  useEffect(() => {
    async function loadTasks() {
      setLoading(true);
      const intakeTasks = await TaskService.getIntakeTasks(medplum, patient);
      setTasks(intakeTasks);
      setLoading(false);
    }
    
    loadTasks();
  }, [medplum, patient]);
  
  if (loading) {
    return <Loader size="sm" />;
  }
  
  if (tasks.length === 0) {
    return <></>; // Don't show anything if no tasks
  }
  
  // Focus on the most recent task
  const latestTask = tasks[0];
  const progressPercentage = latestTask.totalForms === 0 ? 
    0 : Math.round((latestTask.completedForms / latestTask.totalForms) * 100);
  
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in-progress': return 'blue';
      case 'requested': return 'yellow';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };
  
  return (
    <Paper withBorder p="md" mb="xl">
      <Stack gap="md">
        <Group justify="apart">
          <Title order={4}>Intake Process</Title>
          <Badge color={getStatusColor(latestTask.status)}>
            {latestTask.status === 'completed' ? 'Completed' : 
             latestTask.status === 'in-progress' ? 'In Progress' : 
             latestTask.status === 'requested' ? 'Requested' : 'Failed'}
          </Badge>
        </Group>
        
        <Stack gap="xs">
          <Group justify="apart">
            <Text size="sm">Form completion progress:</Text>
            <Text size="sm" fw={500}>
              {latestTask.completedForms} of {latestTask.totalForms} forms
            </Text>
          </Group>
          
          <Progress 
            value={progressPercentage} 
            color="green" 
            size="xl" 
            radius="xl"
            striped={false}
            animated={false}
          />
        </Stack>
        
        {latestTask.requestedBy && (
          <Text size="sm" c="dimmed">
            Requested by: {latestTask.requestedBy}
          </Text>
        )}
        
        {latestTask.createdDate && (
          <Text size="sm" c="dimmed">
            Date requested: {formatDate(latestTask.createdDate)}
          </Text>
        )}
        
        {latestTask.status !== 'completed' && latestTask.totalForms > latestTask.completedForms && (
          <Button 
            variant="light" 
            onClick={() => navigate('/documents')}
            fullWidth
          >
            Continue completing forms
          </Button>
        )}
      </Stack>
    </Paper>
  );
}